import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { API } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const download = async (path, filename) => {
  try {
    const token = localStorage.getItem('token') || '';
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error((await res.json()).detail || 'Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast({ title: e.message || 'Download failed', variant: 'destructive' }); }
};

const StatutoryReportsPage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(1);

  const REPORTS = [
    { country: 'India', title: 'Form 16 (annual TDS)', ext: 'xlsx', period: 'annual',
      go: () => download(`/hr/reports/india/form16.xlsx?year=${year}`, `form16_${year}.xlsx`) },
    { country: 'India', title: 'PF ECR', ext: 'xlsx', period: 'monthly',
      go: () => download(`/hr/reports/india/pf-ecr.xlsx?year=${year}&month=${month}`, `pf_ecr_${year}_${String(month).padStart(2, '0')}.xlsx`) },
    { country: 'India', title: 'TDS 24Q quarterly', ext: 'xlsx', period: 'quarterly',
      go: () => download(`/hr/reports/india/tds-24q.xlsx?year=${year}&quarter=${quarter}`, `tds_24q_${year}_Q${quarter}.xlsx`) },
    { country: 'India', title: 'Professional Tax return', ext: 'xlsx', period: 'monthly',
      go: () => download(`/hr/reports/india/pt-return.xlsx?year=${year}&month=${month}`, `pt_return_${year}_${String(month).padStart(2, '0')}.xlsx`) },
    { country: 'United Kingdom', title: 'P60 (annual)', ext: 'xlsx', period: 'annual',
      go: () => download(`/hr/reports/uk/p60.xlsx?year=${year}`, `p60_${year}.xlsx`) },
    { country: 'United Kingdom', title: 'FPS (monthly RTI)', ext: 'xlsx', period: 'monthly',
      go: () => download(`/hr/reports/uk/fps.xlsx?year=${year}&month=${month}`, `fps_${year}_${String(month).padStart(2, '0')}.xlsx`) },
    { country: 'Malaysia', title: 'EA Form (annual)', ext: 'xlsx', period: 'annual',
      go: () => download(`/hr/reports/my/ea-form.xlsx?year=${year}`, `ea_form_${year}.xlsx`) },
    { country: 'Malaysia', title: 'EPF Form A', ext: 'xlsx', period: 'monthly',
      go: () => download(`/hr/reports/my/epf-form-a.xlsx?year=${year}&month=${month}`, `epf_form_a_${year}_${String(month).padStart(2, '0')}.xlsx`) },
    { country: 'Generic', title: 'Payroll register (PDF)', ext: 'pdf', period: 'monthly',
      go: () => download(`/hr/reports/payroll-register.pdf?year=${year}&month=${month}`, `payroll_register_${year}_${String(month).padStart(2, '0')}.pdf`) },
    { country: 'Generic', title: 'Loan interest register (monthly)', ext: 'xlsx', period: 'monthly',
      go: () => download(`/hr/reports/loan-interest-register.xlsx?year=${year}&month=${month}`, `loan_interest_${year}_${String(month).padStart(2, '0')}.xlsx`) },
    { country: 'Generic', title: 'Loan interest register (full year)', ext: 'xlsx', period: 'annual',
      go: () => download(`/hr/reports/loan-interest-register.xlsx?year=${year}`, `loan_interest_${year}.xlsx`) },
  ];

  const grouped = REPORTS.reduce((a, r) => { (a[r.country] = a[r.country] || []).push(r); return a; }, {});

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Statutory Reports</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>India · UK · Malaysia</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Regulatory-compliant Excel & PDF exports pulled from executed payroll runs.</p>
      </div>
      <div className="flex items-center gap-3 mb-6 flex-wrap p-3 bg-[#f7fbf8] rounded-2xl">
        <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Year</label>
        <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="rpt-year" className="w-24 px-2 py-1.5 border rounded text-sm" />
        <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Month</label>
        <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} data-testid="rpt-month" className="px-2 py-1.5 border rounded text-sm bg-white">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>)}
        </select>
        <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Quarter</label>
        <select value={quarter} onChange={(e) => setQuarter(parseInt(e.target.value, 10))} className="px-2 py-1.5 border rounded text-sm bg-white">
          {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Object.entries(grouped).map(([country, reports]) => (
          <div key={country} className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-[#0b1a2e] text-white text-sm font-semibold">{country}</div>
            <div className="p-2">
              {reports.map((r) => (
                <button key={r.title} onClick={r.go} data-testid={`rpt-${r.country}-${r.title}`}
                  className="w-full flex items-center gap-3 p-3 hover:bg-[#f7fbf8] rounded-lg text-left">
                  {r.ext === 'pdf' ? <FileText className="w-5 h-5 text-red-600 flex-shrink-0" /> : <FileSpreadsheet className="w-5 h-5 text-emerald-700 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-[10px] text-[#0b1a2e]/50 uppercase tracking-wider">{r.period} · {r.ext}</div>
                  </div>
                  <Download className="w-4 h-4 text-[#0f5c3d]" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatutoryReportsPage;
